// runTrigger — P-002: client-wrapper för run-workflow edge-fn.
// Kallas av UI när user trycker "Kör" på en Enterprise-workflow.
//
// Permission-flow:
// - Klient-gate: \`usePlan().limits.canRunProcesses\` sätter om knappen
//   överhuvudtaget visas.
// - Server-gate: edge-fn låter workflow_runs-insert-trigger göra
//   auktoritativ check mot profiles.plan_tier. Non-enterprise → 402
//   \`enterprise-required\`.

import { supabase } from '@/integrations/supabase/client';
import { buildEdgeUrl } from '@/lib/edgeFunctionUrl';

export interface TriggerRunInput {
  workflowId: string;
  triggerData?: unknown;
  triggerKind?: 'manual' | 'test';
}

export interface TriggerRunResult {
  success: boolean;
  runId?: string;
  status?: string;
  error?: string;
  /** HTTP-status från edge-fn (403=not-auth, 402=enterprise-required, etc.) */
  httpStatus?: number;
}

export async function triggerWorkflowRun(
  input: TriggerRunInput,
): Promise<TriggerRunResult> {
  if (!input.workflowId) {
    return { success: false, error: 'missing-workflow-id' };
  }
  const { data: session } = await supabase.auth.getSession();
  const token = session.session?.access_token;
  if (!token) {
    return { success: false, error: 'not-authenticated' };
  }

  const url = buildEdgeUrl('run-workflow');
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        workflow_id: input.workflowId,
        trigger_data: input.triggerData ?? null,
        trigger_kind: input.triggerKind ?? 'manual',
      }),
    });

    if (resp.status === 404) {
      return { success: false, error: 'edge-function-not-deployed', httpStatus: 404 };
    }
    const body = await resp.json().catch(() => ({ error: `http-${resp.status}` }));
    if (!resp.ok) {
      return {
        success: false,
        error: String(body?.error ?? `http-${resp.status}`),
        httpStatus: resp.status,
      };
    }
    return {
      success: true,
      runId: String(body.run_id ?? ''),
      status: String(body.status ?? 'pending'),
    };
  } catch (e) {
    return { success: false, error: (e as Error).message || 'network-error' };
  }
}
